import { createContext, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

const AuthContext = createContext(null);
const domain = process.env.REACT_APP_COGNITO_DOMAIN;
const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
const redirectUri =
  process.env.REACT_APP_COGNITO_REDIRECT_URI || `${window.location.origin}/auth/callback`;
const logoutUri = process.env.REACT_APP_COGNITO_LOGOUT_URI || window.location.origin;
export const ADMIN_SESSION_EXPIRED_EVENT = "msafer:admin-session-expired";

function decodeToken(token) {
  if (!token) return {};
  try {
    const value = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(decodeURIComponent(escape(window.atob(value))));
  } catch {
    return {};
  }
}

function activeStoredToken() {
  const storedToken = localStorage.getItem("msafer_admin_access_token");
  const expiresAt = Number(decodeToken(storedToken).exp || 0) * 1000;
  if (!storedToken || !expiresAt || expiresAt <= Date.now()) {
    localStorage.removeItem("msafer_admin_access_token");
    localStorage.removeItem("msafer_admin_id_token");
    return null;
  }
  return storedToken;
}

function base64Url(buffer) {
  return window
    .btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

async function createPkce() {
  const random = crypto.getRandomValues(new Uint8Array(48));
  const verifier = base64Url(random);
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return { verifier, challenge: base64Url(digest) };
}

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(activeStoredToken);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const configured = Boolean(domain && clientId);

  useEffect(() => {
    const handleExpiredSession = () => {
      localStorage.removeItem("msafer_admin_access_token");
      localStorage.removeItem("msafer_admin_id_token");
      setToken(null);
      setBusy(false);
      setError("انتهت جلسة الإدارة. سجّل الدخول مرة أخرى للمتابعة.");
      window.history.replaceState({}, "", "/");
    };

    window.addEventListener(ADMIN_SESSION_EXPIRED_EVENT, handleExpiredSession);
    return () => window.removeEventListener(ADMIN_SESSION_EXPIRED_EVENT, handleExpiredSession);
  }, []);

  useEffect(() => {
    if (!configured || window.location.pathname !== "/auth/callback") return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const oauthError = params.get("error_description") || params.get("error");
    if (oauthError) {
      setError("هذا البريد غير مصرح له بالدخول إلى لوحة الإدارة");
      window.history.replaceState({}, "", "/");
      return;
    }
    if (!code || state !== sessionStorage.getItem("msafer_admin_oauth_state")) return;
    setBusy(true);
    setError(null);
    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: clientId,
      code,
      redirect_uri: redirectUri,
      code_verifier: sessionStorage.getItem("msafer_admin_pkce") || "",
    });
    fetch(`${domain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    })
      .then((response) => {
        if (!response.ok) throw new Error("token_exchange_failed");
        return response.json();
      })
      .then((payload) => {
        localStorage.setItem("msafer_admin_access_token", payload.access_token);
        localStorage.setItem("msafer_admin_id_token", payload.id_token);
        setToken(payload.access_token);
        window.history.replaceState({}, "", "/dashboard");
      })
      .catch(() => {
        localStorage.removeItem("msafer_admin_access_token");
        localStorage.removeItem("msafer_admin_id_token");
        setToken(null);
        setError("هذا البريد غير مصرح له بالدخول إلى لوحة الإدارة");
      })
      .finally(() => setBusy(false));
  }, [configured]);

  const claims = decodeToken(localStorage.getItem("msafer_admin_id_token") || token);
  const groups = claims["cognito:groups"] || (configured ? [] : ["Admin"]);
  const role = groups.includes("Admin") ? "Admin" : groups.includes("Editor") ? "Editor" : null;

  const login = async () => {
    if (!configured) return;
    setError(null);
    const { verifier, challenge } = await createPkce();
    const state = crypto.randomUUID();
    sessionStorage.setItem("msafer_admin_pkce", verifier);
    sessionStorage.setItem("msafer_admin_oauth_state", state);
    const params = new URLSearchParams({
      response_type: "code",
      client_id: clientId,
      redirect_uri: redirectUri,
      scope: "openid email profile",
      state,
      code_challenge_method: "S256",
      code_challenge: challenge,
      identity_provider: "Google",
    });
    window.location.assign(`${domain}/oauth2/authorize?${params}`);
  };

  const logout = () => {
    localStorage.removeItem("msafer_admin_access_token");
    localStorage.removeItem("msafer_admin_id_token");
    setToken(null);
    if (configured) {
      const params = new URLSearchParams({ client_id: clientId, logout_uri: logoutUri });
      window.location.assign(`${domain}/logout?${params}`);
    }
  };

  const value = useMemo(
    () => ({
      configured,
      authenticated: configured ? Boolean(token && role) : true,
      busy,
      error,
      claims,
      role: role || "Editor",
      canPublish: role === "Admin" || !configured,
      login,
      logout,
    }),
    [configured, token, role, busy, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AdminAuthProvider.propTypes = { children: PropTypes.node.isRequired };

export function useAdminAuth() {
  return useContext(AuthContext);
}
